import { ChangeEvent, useMemo } from 'react'

import cn from 'classnames'
import Skeleton from 'react-loading-skeleton'

import Toggle from '@src/components/Toggle'
import TokenSelectionPill from '@src/components/TokenSelectionPill'
import { getChainType } from '@src/configs/chainInfo'
import { CHAIN_ICON_SIZE, MAX_TOKEN_DISPLAY_DECIMALS, TOKEN_ICON_SIZE } from '@src/constants/exchange'
import { DestinationWalletInput } from '@src/pages/Exchange/components/DestinationWalletInput'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { useExchangeDerivedState } from '@src/pages/Exchange/hooks/useExchangeDerivedState'
import { IExchangeData } from '@src/types/exchange'
import { Address, IToken } from '@src/types/token'
import { formatNumberStringWithCommas } from '@src/utils'
import { computeUSDAmount } from '@src/utils/balance'
import { isFeatureEnabled } from '@src/utils/environment'

interface IToSectionProps {
    buyData: IExchangeData
    sellData: IExchangeData // if you need it for cross-chain logic

    isCustomWallet: boolean
    isCrossChain: boolean
    onToggleCustomWallet: () => void

    destinationAddress: Address | undefined
    validateAddress: (address: Address | undefined, chainId: number) => boolean
    onChangeDestAddress: (event: ChangeEvent<HTMLInputElement>) => void

    /** The read-only text for the "to" amount if you want to display it. */
    buyAmountString: string
    isBuyOutputLoading: boolean
    isQuoteLoading: boolean

    onTokenSelectionChange: (
        newData: { token?: IToken; chainId?: number; value?: bigint },
        type: 'buy' | 'sell'
    ) => void
    onClickAmount?: () => void // e.g. for copying the final buy amount
}

export function ToSection({
    buyData,
    sellData,
    isCustomWallet,
    isCrossChain,
    onToggleCustomWallet,
    destinationAddress,
    validateAddress,
    onChangeDestAddress,
    buyAmountString,
    isBuyOutputLoading,
    isQuoteLoading,
    onTokenSelectionChange,
    onClickAmount,
}: IToSectionProps) {
    const { isBuyBalanceLoading, buyBalance, buyBalanceStabilized } = useExchangeBalances()
    const { isQuoteEnabled } = useExchangeState()
    const { disabledToChains: disabledChains } = useExchangeDerivedState()
    const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

    const BalanceComponent = (
        <>
            {buyBalance && (
                <span className='md:text-md flex items-center justify-end gap-4 text-xs text-mach-sub-text sm:text-sm'>
                    {
                        /* isQuoteEnabled && */ isBuyBalanceLoading || !buyBalanceStabilized ? (
                            <Skeleton containerClassName='w-24' enableAnimation baseColor='#9B9B9B' />
                        ) : (
                            <>
                                Bal: {buyBalance ? buyBalance.formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS) : 0}{' '}
                                {buyData.token.symbol}
                            </>
                        )
                    }
                </span>
            )}
        </>
    )

    const buyBalanceInUSD = useMemo<string | undefined>(() => {
        if (isBuyOutputLoading) {
            return undefined
        } else {
            return `$${formatNumberStringWithCommas(computeUSDAmount(buyData.token, buyData.value)?.toString())}`
        }
    }, [buyData.token, buyData.value, isBuyOutputLoading])

    return (
        <div className='space-y-4 rounded-3xl border-mach-surface-border bg-mach-surface-background p-5'>
            {/* Toggle row floats right - hide on testnet */}
            {!isTestnet && (
                <div className='float-right hidden items-center gap-[6px] pb-2 md:flex'>
                    <span className='text-xs text-mach-sub-text'>Change Recipient</span>
                    <Toggle checked={isCustomWallet} setChecked={onToggleCustomWallet} />
                </div>
            )}

            {/* Clear floats so that this row starts below the floated element */}
            <div className='clear-both flex items-center justify-between'>
                {/* Label row */}
                <span className='text-sm text-mach-accent-forground'>Buy</span>
                <TokenSelectionPill
                    onChange={_d => onTokenSelectionChange(_d, 'buy')}
                    value={buyData}
                    showNetwork
                    tokenIconSize={TOKEN_ICON_SIZE}
                    chainIconSize={CHAIN_ICON_SIZE}
                    disabledChains={disabledChains}
                    buyData={buyData}
                    sellData={sellData}
                    side='buy'
                />
            </div>

            <div className='flex items-center justify-between'>
                <input
                    className={cn(
                        'w-full bg-transparent text-left text-lg md:text-[32px]',
                        // if it's not loading & has a valid output, make it clickable
                        !isBuyOutputLoading && !isQuoteLoading && buyAmountString !== '0' && buyAmountString !== ''
                            ? 'cursor-pointer'
                            : 'cursor-default'
                    )}
                    placeholder={
                        (isBuyOutputLoading || isQuoteLoading) && isQuoteEnabled
                            ? sellData.value > 0
                                ? 'Loading...'
                                : '0'
                            : buyAmountString
                    }
                    value=''
                    readOnly
                    onClick={() => {
                        if (isBuyOutputLoading || isQuoteLoading || buyAmountString === '0' || buyAmountString === '') {
                            return
                        }
                        // If you want to let user copy
                        onClickAmount && onClickAmount()
                    }}
                />
                <TokenSelectionPill
                    onChange={_d => onTokenSelectionChange(_d, 'buy')}
                    value={buyData}
                    tokenIconSize={TOKEN_ICON_SIZE}
                    chainIconSize={CHAIN_ICON_SIZE}
                    disabledChains={disabledChains}
                    buyData={buyData}
                    sellData={sellData}
                    side='buy'
                />
            </div>

            <div className='flex items-center justify-between'>
                {/* USD value always floats right */}
                <span className='md:text-md flex items-center gap-4 text-xs text-mach-sub-text sm:text-sm'>
                    {((): JSX.Element => {
                        let elem: JSX.Element = <></>
                        if (isQuoteEnabled && isBuyOutputLoading && sellData.value > BigInt(0)) {
                            elem = <Skeleton containerClassName='w-16' enableAnimation baseColor='#9B9B9B' />
                        } else if (!isQuoteEnabled || sellData.value === BigInt(0)) {
                            elem = <>$0</>
                        } else if (buyData.token.price_current === undefined) {
                            // If sell Balance is not 0, but we cannot compute USD balance for some reason,
                            // do not display anything instead of displaying $0
                            elem = <></>
                        } else {
                            elem = <>{buyBalanceInUSD}</>
                        }
                        return elem
                    })()}
                </span>

                {/* Balance */}
                <div>{BalanceComponent}</div>
            </div>

            {(isCustomWallet || isCrossChain) && (
                <DestinationWalletInput
                    isVisible={isCustomWallet || isCrossChain}
                    destinationAddress={destinationAddress}
                    isValidDestination={validateAddress(destinationAddress, buyData.chainId)}
                    chainName={getChainType(buyData.chainId)}
                    onChangeAddress={onChangeDestAddress}
                />
            )}
        </div>
    )
}
